import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import TankAvatar from './TankAvatar';
import {
  AlertTriangle,
  X,
  Eye,
  ChevronRight,
  Bell,
  BellRing,
  MapPin,
  Volume2,
  VolumeX
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Alert notification component
const IncidentAlertNotification = ({ alert, onDismiss, onViewOverwatch }) => {
  const [isVisible, setIsVisible] = useState(true);
  
  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(() => onDismiss(alert.id), 300);
  };
  
  if (!isVisible) return null;
  
  return (
    <div 
      className={`
        animate-in slide-in-from-right duration-300
        bg-gradient-to-r ${alert.is_critical ? 'from-red-900/95 to-slate-900/95' : 'from-orange-900/95 to-slate-900/95'}
        border ${alert.is_critical ? 'border-red-500/50' : 'border-orange-500/50'}
        rounded-lg p-4 shadow-xl backdrop-blur-sm
        max-w-md
      `}
      data-testid="incident-alert"
    >
      <div className="flex items-start gap-3">
        {/* Tank Avatar */}
        <div className="flex-shrink-0">
          <TankAvatar size="sm" showTooltip={false} />
        </div>
        
        {/* Alert Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge 
              className={`text-[10px] ${
                alert.is_critical 
                  ? 'bg-red-600 text-white animate-pulse' 
                  : 'bg-orange-600 text-white'
              }`}
            >
              {alert.is_critical ? 'CRITICAL' : 'ALERT'}
            </Badge>
            <span className="text-xs text-slate-400">
              {alert.distance_miles < 1 
                ? `${(alert.distance_miles * 5280).toFixed(0)} ft away` 
                : `${alert.distance_miles.toFixed(1)} mi away`
              }
            </span>
          </div>
          
          <p className="text-sm text-white font-medium mb-2">
            {alert.message}
          </p>
          
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className={`${
                alert.is_critical 
                  ? 'bg-red-600 hover:bg-red-700' 
                  : 'bg-orange-600 hover:bg-orange-700'
              } text-white text-xs`}
              onClick={() => onViewOverwatch(alert)}
            >
              <Eye className="w-3 h-3 mr-1" />
              View in OverWatch
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-slate-400 hover:text-white text-xs"
              onClick={handleDismiss}
            >
              Dismiss
            </Button>
          </div>
        </div>
        
        {/* Close button */}
        <button 
          onClick={handleDismiss}
          className="text-slate-400 hover:text-white"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// Alert container that manages multiple alerts
export const IncidentAlertContainer = ({ userLocation, enabled = true }) => {
  const { token } = useAuth();
  const { isPaid } = useSubscription();
  const navigate = useNavigate();
  
  const [alerts, setAlerts] = useState([]);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [checking, setChecking] = useState(false);
  
  // Audio for alerts
  const playAlertSound = useCallback(() => {
    if (soundEnabled) {
      // Create a simple beep using Web Audio API
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = 800;
        oscillator.type = 'sine';
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
        
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.5);
      } catch (err) {
        console.log('Audio not available');
      }
    }
  }, [soundEnabled]);
  
  // Check for alerts periodically
  const checkAlerts = useCallback(async () => {
    if (!token || !userLocation || !enabled || !isPaid()) return;
    
    setChecking(true);
    try {
      const res = await fetch(
        `${API_URL}/api/tanks-eyes/alerts/check?lat=${userLocation.lat}&lng=${userLocation.lng}&radius=10`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (res.ok) {
        const data = await res.json();
        if (data.has_alerts && data.alerts.length > 0) {
          // Add new alerts
          setAlerts(prev => {
            const existingIds = new Set(prev.map(a => a.id));
            const newAlerts = data.alerts.filter(a => !existingIds.has(a.id));
            
            if (newAlerts.length > 0) {
              playAlertSound();
            }
            
            return [...newAlerts, ...prev].slice(0, 5); // Keep max 5 alerts
          });
        }
      }
    } catch (err) {
      console.error('Failed to check alerts:', err);
    } finally {
      setChecking(false);
    }
  }, [token, userLocation, enabled, isPaid, playAlertSound]);
  
  // Poll for alerts every 30 seconds
  useEffect(() => {
    if (!enabled || !isPaid()) return;
    
    // Initial check
    checkAlerts();
    
    // Set up interval
    const interval = setInterval(checkAlerts, 30000);
    
    return () => clearInterval(interval);
  }, [checkAlerts, enabled, isPaid]);
  
  // Handle dismiss
  const handleDismiss = (alertId) => {
    setAlerts(prev => prev.filter(a => a.id !== alertId));
    
    // Acknowledge on server
    fetch(`${API_URL}/api/tanks-eyes/alerts/acknowledge?alert_id=${alertId}`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    }).catch(() => {});
  };
  
  // Handle view in OverWatch
  const handleViewOverwatch = (alert) => {
    navigate(`/overwatch?lat=${alert.lat}&lng=${alert.lng}&zoom=14&incident=${alert.incident_id}`);
    handleDismiss(alert.id);
  };
  
  if (!enabled || !isPaid() || alerts.length === 0) return null;
  
  return (
    <div 
      className="fixed top-20 right-4 z-50 space-y-3 max-h-[calc(100vh-100px)] overflow-y-auto"
      data-testid="incident-alert-container"
    >
      {/* Sound toggle */}
      <div className="flex justify-end mb-2">
        <button
          onClick={() => setSoundEnabled(!soundEnabled)}
          className={`p-2 rounded-full ${soundEnabled ? 'bg-teal-500/20 text-teal-400' : 'bg-slate-800 text-slate-500'}`}
          title={soundEnabled ? 'Mute alerts' : 'Enable alert sounds'}
        >
          {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
        </button>
      </div>
      
      {/* Alerts */}
      {alerts.map(alert => (
        <IncidentAlertNotification
          key={alert.id}
          alert={alert}
          onDismiss={handleDismiss}
          onViewOverwatch={handleViewOverwatch}
        />
      ))}
    </div>
  );
};

// Alert bell icon for header
export const AlertBellIcon = ({ alertCount = 0, onClick }) => {
  return (
    <button
      onClick={onClick}
      className="relative p-2 rounded-lg hover:bg-slate-800 transition-colors"
      data-testid="alert-bell"
    >
      {alertCount > 0 ? (
        <BellRing className="w-5 h-5 text-red-400 animate-pulse" />
      ) : (
        <Bell className="w-5 h-5 text-slate-400" />
      )}
      {alertCount > 0 && (
        <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-[10px] text-white font-bold">
          {alertCount > 9 ? '9+' : alertCount}
        </span>
      )}
    </button>
  );
};

// Demo alert trigger button
export const DemoAlertButton = ({ userLocation, onAlert }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  
  const triggerDemo = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const lat = userLocation?.lat || 32.7157;
      const lng = userLocation?.lng || -117.1611;
      
      const res = await fetch(
        `${API_URL}/api/tanks-eyes/alerts/demo?lat=${lat}&lng=${lng}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (res.ok) {
        const data = await res.json();
        onAlert(data.alert);
      }
    } catch (err) {
      console.error('Failed to get demo alert:', err);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={triggerDemo}
      disabled={loading}
      className="border-red-500/50 text-red-400 hover:bg-red-500/10"
      data-testid="demo-alert-btn"
    >
      <AlertTriangle className="w-4 h-4 mr-2" />
      {loading ? 'Generating...' : 'Test Alert'}
    </Button>
  );
};

export default IncidentAlertContainer;
